/**
 * LiveKit room provider — connect flow:
 *   1) POST /livekit/{role}/token  → JWT + server url
 *   2) Mount LiveKitRoom with token (connect=true)
 *   3) LiveKitBridge applies initial mic/cam once Connected
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  LiveKitRoom as LiveKitRoomPrimitive,
  RoomAudioRenderer as RoomAudioRendererPrimitive,
  useConnectionState,
  useLocalParticipant,
  useParticipants,
  useRoomContext,
} from "@livekit/components-react";
import {
  ConnectionState,
  ConnectionQuality,
  DisconnectReason,
  RoomEvent,
  Track,
} from "livekit-client";
import {
  type LiveSessionRole,
} from "./config";
import { fetchLiveKitToken, isLiveKitTokenPending } from "./fetchLiveKitToken";
import { liveKitRoomOptions, screenShareCaptureOptions } from "./mediaPublish";
import {
  DeviceCheckScreen,
  type DeviceCheckSelection,
} from "./DeviceCheckScreen";
import { joinDefaultsForRole } from "./sessionSettings";
import {
  enableLocalCamera,
  enableLocalMicrophone,
  formatMediaError,
  type MediaKind,
} from "./mediaDevices";
import { useSelectiveTrackSubscription } from "./useSelectiveTrackSubscription";

export type OfflineNotice = {
  title: string;
  reason: string;
  reconnecting: boolean;
};

/** Map LiveKit disconnect reasons to trainer-facing copy. */
function friendlyTrainerDisconnectReason(
  reason?: DisconnectReason,
): string | null {
  if (reason == null) return null;
  if (reason === DisconnectReason.CLIENT_INITIATED) return null;
  if (reason === DisconnectReason.DUPLICATE_IDENTITY) {
    return "You joined this session from another device or tab. This tab was disconnected. Continue here to teach from this tab, or close this message.";
  }
  if (reason === DisconnectReason.JOIN_FAILURE) {
    return "Could not stay connected to the live session. Check your network and try again.";
  }
  if (
    reason === DisconnectReason.ROOM_DELETED ||
    reason === DisconnectReason.ROOM_CLOSED ||
    reason === DisconnectReason.SERVER_SHUTDOWN
  ) {
    return "The live session room was closed on the server.";
  }
  if (reason === DisconnectReason.PARTICIPANT_REMOVED) {
    return "This trainer was removed from the session.";
  }
  if (reason === DisconnectReason.STATE_MISMATCH) {
    return "The session connection was reset. Rejoin to continue teaching.";
  }
  if (reason === DisconnectReason.SIGNAL_CLOSE) {
    return "The live session connection closed unexpectedly.";
  }
  return "Lost connection to the live session.";
}

import {
  friendlyLiveKitConnectError,
  isLiveKitRoomStartingMessage,
  isSessionEndedLiveKitError,
} from "./liveKitConnectErrors";
import { useNavigate } from "react-router-dom";
import { getViewSessionsPath } from "../LMS/TR/files/sessionUtils/sessionUtils";
// CRA + TS 4.9 reject LiveKit's ReactNode return type for JSX components.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const LiveKitRoom = LiveKitRoomPrimitive as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const RoomAudioRenderer = RoomAudioRendererPrimitive as any;

export type LiveKitConnectionQualityLabel =
  | "Excellent"
  | "Good"
  | "Poor"
  | "Lost"
  | "Connecting"
  | "Offline"
  | "Unknown";

export type LiveKitSessionContextValue = {
  enabled: boolean;
  inRoom: boolean;
  connected: boolean;
  connecting: boolean;
  error: string | null;
  mediaError: string | null;
  mediaErrorKind: MediaKind | null;
  participantCount: number;
  /** LiveKit local connection quality label for the top bar. */
  connectionQualityLabel: LiveKitConnectionQualityLabel;
  /** Mid-session offline / disconnect popup (null when connected). */
  offlineNotice: OfflineNotice | null;
  /** True only while LiveKit room recording / egress is active. */
  isRecording: boolean;
  isMicrophoneEnabled: boolean;
  isCameraEnabled: boolean;
  isScreenShareEnabled: boolean;
  /** True for assigned trainer / admin; false for co-trainers. */
  isPrimaryTrainer: boolean;
  toggleMicrophone: () => Promise<boolean>;
  toggleCamera: () => Promise<boolean>;
  /** Pass `{ withAudio: true }` to include system/tab audio when starting a share. */
  toggleScreenShare: (options?: { withAudio?: boolean }) => Promise<boolean>;
  /** Stop LiveKit reconnect and disconnect — used when leaving/removed. */
  stopRoom: () => void;
};

const fallbackValue: LiveKitSessionContextValue = {
  enabled: false,
  inRoom: false,
  connected: false,
  connecting: false,
  error: null,
  mediaError: null,
  mediaErrorKind: null,
  participantCount: 0,
  connectionQualityLabel: "Offline",
  offlineNotice: null,
  isRecording: false,
  isPrimaryTrainer: false,
  isMicrophoneEnabled: false,
  isCameraEnabled: false,
  isScreenShareEnabled: false,
  toggleMicrophone: async () => false,
  toggleCamera: async () => false,
  toggleScreenShare: async () => false,
  stopRoom: () => undefined,
};

const LiveKitSessionContext =
  createContext<LiveKitSessionContextValue>(fallbackValue);

export function useLiveKitSession(): LiveKitSessionContextValue {
  return useContext(LiveKitSessionContext);
}

function LiveKitBridge({
  children,
  startWithAudio,
  startWithVideo,
  audioDeviceId,
  videoDeviceId,
  audioOutputDeviceId,
  isPrimaryTrainer,
}: {
  children: ReactNode;
  startWithAudio: boolean;
  startWithVideo: boolean;
  audioDeviceId?: string;
  videoDeviceId?: string;
  audioOutputDeviceId?: string;
  isPrimaryTrainer: boolean;
}) {
  // Camera subscribe targets come from LiveSessionRoom (on-screen tiles only).
  useSelectiveTrackSubscription({ enabled: false });
  const connectionState = useConnectionState();
  const participants = useParticipants();
  const room = useRoomContext();
  const {
    localParticipant,
    isMicrophoneEnabled,
    isCameraEnabled,
    isScreenShareEnabled,
  } = useLocalParticipant();
  const [initialMediaReady, setInitialMediaReady] = useState(false);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [mediaErrorKind, setMediaErrorKind] = useState<MediaKind | null>(null);

  useEffect(() => {
    if (!mediaError || mediaErrorKind !== "screen") return;
    const id = window.setTimeout(() => {
      setMediaError(null);
      setMediaErrorKind(null);
    }, 12_000);
    return () => window.clearTimeout(id);
  }, [mediaError, mediaErrorKind]);

  const [connectionQuality, setConnectionQuality] = useState<ConnectionQuality>(
    ConnectionQuality.Unknown,
  );
  const [isRecording, setIsRecording] = useState(false);
  const [hasBeenConnected, setHasBeenConnected] = useState(false);
  const [leftVoluntarily, setLeftVoluntarily] = useState(false);
  const [disconnectReasonText, setDisconnectReasonText] = useState<
    string | null
  >(null);
  const [browserOnline, setBrowserOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );

  const connected = connectionState === ConnectionState.Connected;
  const connecting =
    connectionState === ConnectionState.Connecting ||
    connectionState === ConnectionState.Reconnecting;

  useEffect(() => {
    if (connected) {
      setHasBeenConnected(true);
      setLeftVoluntarily(false);
      setDisconnectReasonText(null);
    }
  }, [connected]);

  // Unlock remote speaker playback without requiring the StartAudio banner.
  useEffect(() => {
    if (!room || !connected) return;
    let cancelled = false;

    const tryStart = () => {
      if (cancelled || room.canPlaybackAudio) return;
      void room.startAudio().catch(() => {
        /* autoplay still blocked — gesture listener below will retry */
      });
    };

    tryStart();

    const onGesture = () => {
      void room.startAudio().finally(() => {
        if (room.canPlaybackAudio) {
          window.removeEventListener("pointerdown", onGesture, true);
          window.removeEventListener("keydown", onGesture, true);
          window.removeEventListener("touchstart", onGesture, true);
        }
      });
    };

    window.addEventListener("pointerdown", onGesture, true);
    window.addEventListener("keydown", onGesture, true);
    window.addEventListener("touchstart", onGesture, true);
    return () => {
      cancelled = true;
      window.removeEventListener("pointerdown", onGesture, true);
      window.removeEventListener("keydown", onGesture, true);
      window.removeEventListener("touchstart", onGesture, true);
    };
  }, [room, connected]);

  useEffect(() => {
    const onOnline = () => setBrowserOnline(true);
    const onOffline = () => setBrowserOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  useEffect(() => {
    if (!room) return;
    setIsRecording(Boolean(room.isRecording));
    setConnectionQuality(
      localParticipant?.connectionQuality ?? ConnectionQuality.Unknown,
    );

    const onQuality = (
      quality: ConnectionQuality,
      participant?: { identity?: string },
    ) => {
      if (
        participant &&
        room.localParticipant &&
        participant.identity !== room.localParticipant.identity
      ) {
        return;
      }
      setConnectionQuality(quality);
    };
    const onRecording = (active: boolean) => {
      setIsRecording(Boolean(active));
    };
    const onDisconnected = (reason?: DisconnectReason) => {
      if (reason === DisconnectReason.CLIENT_INITIATED) {
        setLeftVoluntarily(true);
        return;
      }
      const text = friendlyTrainerDisconnectReason(reason);
      if (text) setDisconnectReasonText(text);
    };
    const onReconnected = () => {
      setDisconnectReasonText(null);
    };

    room.on(RoomEvent.ConnectionQualityChanged, onQuality);
    room.on(RoomEvent.RecordingStatusChanged, onRecording);
    room.on(RoomEvent.Disconnected, onDisconnected);
    room.on(RoomEvent.Reconnected, onReconnected);
    return () => {
      room.off(RoomEvent.ConnectionQualityChanged, onQuality);
      room.off(RoomEvent.RecordingStatusChanged, onRecording);
      room.off(RoomEvent.Disconnected, onDisconnected);
      room.off(RoomEvent.Reconnected, onReconnected);
    };
  }, [room, localParticipant]);

  const connectionQualityLabel = useMemo<LiveKitConnectionQualityLabel>(() => {
    if (!browserOnline) return "Offline";
    if (connecting) return "Connecting";
    if (!connected) return "Offline";
    switch (connectionQuality) {
      case ConnectionQuality.Excellent:
        return "Excellent";
      case ConnectionQuality.Good:
        return "Good";
      case ConnectionQuality.Poor:
        return "Poor";
      case ConnectionQuality.Lost:
        return "Lost";
      default:
        return "Unknown";
    }
  }, [browserOnline, connecting, connected, connectionQuality]);

  const offlineNotice = useMemo<OfflineNotice | null>(() => {
    if (!hasBeenConnected || leftVoluntarily) return null;
    if (!browserOnline) {
      return {
        title: "You are offline",
        reason:
          "Your internet connection was lost. Check your Wi‑Fi or network and try again.",
        reconnecting: connecting,
      };
    }
    if (connecting && !connected) {
      return {
        title: "You are disconnected",
        reason:
          disconnectReasonText ||
          "Connection dropped. Trying to reconnect to the live session.",
        reconnecting: true,
      };
    }
    if (!connected) {
      if (
        disconnectReasonText == null &&
        connectionState === ConnectionState.Disconnected
      ) {
        return {
          title: "You are disconnected",
          reason: "Lost connection to the live session. Check your network and try again.",
          reconnecting: false,
        };
      }
      if (disconnectReasonText) {
        return {
          title: "You are disconnected",
          reason: disconnectReasonText,
          reconnecting: false,
        };
      }
    }
    return null;
  }, [
    browserOnline,
    connected,
    connecting,
    connectionState,
    disconnectReasonText,
    hasBeenConnected,
    leftVoluntarily,
  ]);

  useEffect(() => {
    if (!connected || initialMediaReady || !localParticipant) return;
    const applyInitialMedia = async () => {
      try {
        // ---------------------------------------------------------------
        // step 1: Apply devices chosen on the pre-join check screen
        // ---------------------------------------------------------------
        if (audioDeviceId) {
          try {
            await room.switchActiveDevice("audioinput", audioDeviceId, false);
          } catch {
            /* ignore — enable helper will fall back */
          }
        }
        if (videoDeviceId) {
          try {
            await room.switchActiveDevice("videoinput", videoDeviceId, false);
          } catch {
            /* ignore */
          }
        }
        if (audioOutputDeviceId) {
          try {
            await room.switchActiveDevice(
              "audiooutput",
              audioOutputDeviceId,
              false,
            );
          } catch {
            /* ignore — not all browsers support sink routing */
          }
        }

        // ---------------------------------------------------------------
        // step 2: Enable mic/cam per pre-join toggles
        // ---------------------------------------------------------------
        if (startWithAudio) {
          await enableLocalMicrophone(localParticipant, room);
        }
        if (startWithVideo) {
          await enableLocalCamera(localParticipant, room);
        }
        setMediaError(null);
        setMediaErrorKind(null);
      } catch (err) {
        const kind: MediaKind = startWithVideo ? "video" : "audio";
        setMediaErrorKind(kind);
        setMediaError(formatMediaError(err, kind));
      } finally {
        setInitialMediaReady(true);
      }
    };
    void applyInitialMedia();
  }, [
    connected,
    initialMediaReady,
    localParticipant,
    startWithAudio,
    startWithVideo,
    audioDeviceId,
    videoDeviceId,
    audioOutputDeviceId,
    room,
  ]);

  const toggleMicrophone = useCallback(async () => {
    if (!localParticipant) return false;
    try {
      if (isMicrophoneEnabled) {
        await localParticipant.setMicrophoneEnabled(false);
      } else {
        await enableLocalMicrophone(localParticipant, room);
      }
      setMediaError(null);
      setMediaErrorKind(null);
      return !isMicrophoneEnabled;
    } catch (err) {
      setMediaErrorKind("audio");
      setMediaError(formatMediaError(err, "audio"));
      return false;
    }
  }, [isMicrophoneEnabled, localParticipant, room]);

  const toggleCamera = useCallback(async () => {
    if (!localParticipant) return false;
    try {
      if (isCameraEnabled) {
        await localParticipant.setCameraEnabled(false);
      } else {
        await enableLocalCamera(localParticipant, room);
      }
      setMediaError(null);
      setMediaErrorKind(null);
      return !isCameraEnabled;
    } catch (err) {
      setMediaErrorKind("video");
      setMediaError(formatMediaError(err, "video"));
      return false;
    }
  }, [isCameraEnabled, localParticipant, room]);

  const toggleScreenShare = useCallback(
    async (options?: { withAudio?: boolean }) => {
      if (!localParticipant || !room) return false;
      try {
        const next = !isScreenShareEnabled;
        if (next) {
          // Only one active screen share in the room at a time.
          const someoneElseSharing = Array.from(
            room.remoteParticipants.values(),
          ).some((p) => {
            const pub = p.getTrackPublication(Track.Source.ScreenShare);
            return Boolean(pub?.track && !pub.isMuted);
          });
          if (someoneElseSharing) {
            setMediaErrorKind("screen");
            setMediaError(
              "Someone else is already sharing their screen. Stop their share (or revoke presenter) before sharing yours.",
            );
            return false;
          }
          // Request display audio so Chrome offers tab/system audio.
          // Use audio: true (not mic constraints) — mic-style options
          // can prevent Chrome from capturing tab/system audio.
          const withAudio = options?.withAudio !== false;
          await localParticipant.setScreenShareEnabled(true, {
            audio: withAudio,
            systemAudio: withAudio ? "include" : "exclude",
            suppressLocalAudioPlayback: false,
            // Exclude this tab from capture so local preview/fullscreen
            // does not create a recursive feedback loop.
            selfBrowserSurface: "exclude",
            surfaceSwitching: "include",
            ...screenShareCaptureOptions,
          });

          // If the user skipped "Share tab/system audio" in Chrome, warn them.
          if (withAudio) {
            // Publication can land a tick after setScreenShareEnabled resolves.
            await new Promise((r) => setTimeout(r, 250));
            const screenAudioPub = localParticipant.getTrackPublication(
              Track.Source.ScreenShareAudio,
            );
            const hasScreenAudio = Boolean(
              screenAudioPub?.track && !screenAudioPub.isMuted,
            );
            if (!hasScreenAudio) {
              setMediaErrorKind("screen");
              setMediaError(
                "Screen shared without audio. Stop share, click Share Screen again, pick a Chrome tab (or Entire screen), and enable “Also share tab audio” / “Share system audio”.",
              );
              return true;
            }
          }
        } else {
          await localParticipant.setScreenShareEnabled(false);
        }
        setMediaError(null);
        setMediaErrorKind(null);
        return next;
      } catch (err) {
        setMediaErrorKind("screen");
        setMediaError(formatMediaError(err, "screen"));
        return false;
      }
    },
    [isScreenShareEnabled, localParticipant, room],
  );

  const stopRoom = useCallback(() => {
    setLeftVoluntarily(true);
    try {
      room?.disconnect();
    } catch {
      /* ignore */
    }
  }, [room]);

  const value = useMemo<LiveKitSessionContextValue>(
    () => ({
      enabled: true,
      inRoom: true,
      connected,
      connecting,
      error: null,
      mediaError,
      mediaErrorKind,
      participantCount: participants.length,
      connectionQualityLabel,
      offlineNotice,
      isRecording,
      isPrimaryTrainer,
      isMicrophoneEnabled,
      isCameraEnabled,
      isScreenShareEnabled,
      toggleMicrophone,
      toggleCamera,
      toggleScreenShare,
      stopRoom,
    }),
    [
      connected,
      connecting,
      connectionQualityLabel,
      offlineNotice,
      isCameraEnabled,
      isMicrophoneEnabled,
      isRecording,
      isPrimaryTrainer,
      isScreenShareEnabled,
      mediaError,
      mediaErrorKind,
      participants.length,
      stopRoom,
      toggleCamera,
      toggleMicrophone,
      toggleScreenShare,
    ]
  );

  return (
    <LiveKitSessionContext.Provider value={value}>
      {/* Without this, remote mics never play — room connects but nobody can hear. */}
      <RoomAudioRenderer volume={1} />
      {children}
    </LiveKitSessionContext.Provider>
  );
}

export function LiveKitSessionProvider({
  children,
  sessionId,
  role,
  displayName,
  startWithAudio,
  startWithVideo,
  sessionTitle,
  onDeviceCheckCancel,
  skipDeviceCheck = false,
}: {
  children: ReactNode;
  sessionId: string;
  role: LiveSessionRole;
  displayName: string;
  startWithAudio: boolean;
  startWithVideo: boolean;
  sessionTitle?: string;
  onDeviceCheckCancel?: () => void;
  skipDeviceCheck?: boolean;
}) {
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(null);
  // Do not seed with REACT_APP_LIVEKIT_URL — shared SFU is often down (502) and
  // would show Offline before/without the per-session token URL.
  const [serverUrl, setServerUrl] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [preparingMessage, setPreparingMessage] = useState<string | null>(null);
  const [deviceSelection, setDeviceSelection] =
    useState<DeviceCheckSelection | null>(
      skipDeviceCheck
        ? {
            audioEnabled: startWithAudio,
            videoEnabled: startWithVideo,
          }
        : null,
    );
  const [resolvedAudioDefault, setResolvedAudioDefault] =
    useState(startWithAudio);
  const [resolvedVideoDefault, setResolvedVideoDefault] =
    useState(startWithVideo);
  const [isPrimaryTrainer, setIsPrimaryTrainer] = useState(false);
  /** Cap soft-pending polls so a dead SFU cannot trap the trainer forever. */
  const MAX_TOKEN_POLL_ATTEMPTS = 40;

  // ---------------------------------------------------------------
  // step: leave join shell — Back on hard error only (not mid Connecting)
  // ---------------------------------------------------------------
  const leaveJoinShell = useCallback(() => {
    if (onDeviceCheckCancel) {
      onDeviceCheckCancel();
      return;
    }
    navigate(getViewSessionsPath(), { replace: true });
  }, [navigate, onDeviceCheckCancel]);

  useEffect(() => {
    let cancelled = false;
    let retryTimer: number | undefined;

    //-------------------------------------------------
    // step:1 mint token (poll while SFU is still starting)
    //-------------------------------------------------
    const applyToken = (data: Awaited<ReturnType<typeof fetchLiveKitToken>>) => {
      setToken(data.token);
      // ---------------------------------------------------------------
      // step 1a: Connect only to token url (per-session SFU).
      // Never fall back to REACT_APP_LIVEKIT_URL — shared host is often 502.
      // ---------------------------------------------------------------
      const fromToken = String(data.url || "").trim();
      if (!fromToken) {
        throw new Error(
          "LiveKit server URL missing from token response. Re-start the session so the media pod can provision.",
        );
      }
      // Shared SFU host is often 502 — require per-session s{N} URL.
      try {
        const host = new URL(
          fromToken.replace(/^wss:/i, "https:"),
        ).hostname.toLowerCase();
        const isPerSession = /^s\d+\./i.test(host);
        const isSharedBare =
          host === "livekit.exskilence.com" ||
          (!isPerSession && host.startsWith("livekit."));
        if (isSharedBare) {
          throw new Error(
            "Media pod is not ready yet (shared LiveKit host). Wait for the green pod status, then Join again.",
          );
        }
      } catch (urlErr) {
        if (
          urlErr instanceof Error &&
          urlErr.message.includes("Media pod is not ready")
        ) {
          throw urlErr;
        }
      }
      setServerUrl(fromToken);
      setIsPrimaryTrainer(data.is_primary_trainer === true);
      // ---------------------------------------------------------------
      // step 1b: Apply trainer-configured join mic/camera defaults
      // ---------------------------------------------------------------
      const defaults = joinDefaultsForRole(data.session_settings, role);
      setResolvedAudioDefault(defaults.microphone);
      setResolvedVideoDefault(defaults.camera);
      if (skipDeviceCheck) {
        setDeviceSelection({
          audioEnabled: defaults.microphone,
          videoEnabled: defaults.camera,
        });
      }
      setPreparingMessage(null);
      setError(null);
      setLoading(false);
    };

    // ---------------------------------------------------------------
    // step 1c: Soft-pending retry — stop after MAX (session-99 trap).
    // Does not change healthy joins that succeed before the cap.
    // ---------------------------------------------------------------
    const scheduleRetry = (attempt: number) => {
      if (attempt + 1 >= MAX_TOKEN_POLL_ATTEMPTS) {
        setPreparingMessage(null);
        setError(
          "Live room did not become ready in time. Go back and try again, or restart the session.",
        );
        setLoading(false);
        return;
      }
      const delayMs = Math.min(10_000, 2_000 + attempt * 1_000);
      retryTimer = window.setTimeout(() => {
        void loadToken(attempt + 1);
      }, delayMs);
    };

    const loadToken = async (attempt = 0) => {
      if (cancelled) return;
      if (attempt === 0) {
        setLoading(true);
        setError(null);
        setPreparingMessage(null);
      }
      try {
        const data = await fetchLiveKitToken({
          sessionId,
          role,
          displayName,
        });
        if (cancelled) return;
        if (isLiveKitTokenPending(data)) {
          const info =
            String(data.message || "").trim() ||
            "LiveKit room is preparing. Please wait a moment.";
          setPreparingMessage(info);
          setLoading(true);
          setError(null);
          scheduleRetry(attempt);
          return;
        }
        applyToken(data);
      } catch (tokenError) {
        if (cancelled) return;
        const friendly = friendlyLiveKitConnectError(tokenError);
        if (
          isLiveKitRoomStartingMessage(friendly) &&
          !isSessionEndedLiveKitError(friendly)
        ) {
          setPreparingMessage(friendly);
          setLoading(true);
          setError(null);
          scheduleRetry(attempt);
          return;
        }
        setPreparingMessage(null);
        setError(friendly);
        setLoading(false);
      }
    };

    void loadToken();
    return () => {
      cancelled = true;
      if (retryTimer != null) window.clearTimeout(retryTimer);
    };
  }, [displayName, role, sessionId, skipDeviceCheck]);

  //-------------------------------------------------
  // step:2 wait until we have JWT + websocket URL
  // Full-page shell — avoid blank gap before LiveKitRoom mounts children.
  //-------------------------------------------------
  if (loading || (!error && (!token || !serverUrl))) {
    return (
      <LiveKitSessionContext.Provider
        value={{
          ...fallbackValue,
          enabled: true,
          connecting: true,
          error,
        }}
      >
        <div
          style={{
            minHeight: "100dvh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            background: "#0b0d12",
            color: "#94a3b8",
            fontSize: 14,
            fontWeight: 600,
            padding: 24,
            textAlign: "center",
          }}
        >
          <p style={{ margin: 0 }}>
            {preparingMessage
              ? "Preparing live room…"
              : "Connecting…"}
          </p>
          {preparingMessage ? (
            <p
              style={{
                margin: 0,
                maxWidth: 420,
                fontWeight: 500,
                color: "#64748b",
                fontSize: 13,
              }}
            >
              {/paused|expected resume/i.test(preparingMessage)
                ? "Resuming session and starting the media pod. Please wait…"
                : preparingMessage}
            </p>
          ) : null}
        </div>
      </LiveKitSessionContext.Provider>
    );
  }

  if (error || !token || !serverUrl) {
    const message =
      error || "LiveKit server URL missing from token response";
    const sessionEnded = isSessionEndedLiveKitError(message);
    return (
      <LiveKitSessionContext.Provider
        value={{
          ...fallbackValue,
          enabled: true,
          error: message,
        }}
      >
        <div
          style={{
            minHeight: "100dvh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            background: "#0b0d12",
            color: sessionEnded ? "#fcd34d" : "#fca5a5",
            fontSize: 14,
            fontWeight: 600,
            padding: 24,
            textAlign: "center",
          }}
        >
          <p style={{ margin: 0, maxWidth: 420 }}>{message}</p>
          {/* step 2a: Always offer Back; Try again only when session is not ended */}
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <button
              type="button"
              onClick={leaveJoinShell}
              style={{
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: 8,
                padding: "8px 14px",
                background: "rgba(255,255,255,0.06)",
                color: "#e2e8f0",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Back to sessions
            </button>
            {!sessionEnded ? (
              <button
                type="button"
                onClick={() => window.location.reload()}
                style={{
                  border: "1px solid rgba(255,255,255,0.2)",
                  borderRadius: 8,
                  padding: "8px 14px",
                  background: "rgba(255,255,255,0.06)",
                  color: "#e2e8f0",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Try again
              </button>
            ) : null}
          </div>
        </div>
      </LiveKitSessionContext.Provider>
    );
  }

  //-------------------------------------------------
  // step:3 pre-join device check (before SFU connect)
  //-------------------------------------------------
  if (!deviceSelection) {
    return (
      <DeviceCheckScreen
        displayName={displayName}
        sessionTitle={sessionTitle}
        role={role}
        defaultAudioEnabled={resolvedAudioDefault}
        defaultVideoEnabled={resolvedVideoDefault}
        onCancel={onDeviceCheckCancel}
        onJoin={(selection) => setDeviceSelection(selection)}
      />
    );
  }

  //-------------------------------------------------
  // step:4 connect room, then bridge applies media + exposes controls
  //-------------------------------------------------
  return (
    <LiveKitRoom
      serverUrl={serverUrl}
      token={token}
      connect
      audio={false}
      video={false}
      options={liveKitRoomOptions}
      connectOptions={{
        autoSubscribe: false,
      }}
      style={{ display: "contents" }}
    >
      <LiveKitBridge
        startWithAudio={deviceSelection.audioEnabled}
        startWithVideo={deviceSelection.videoEnabled}
        audioDeviceId={deviceSelection.audioDeviceId}
        videoDeviceId={deviceSelection.videoDeviceId}
        audioOutputDeviceId={deviceSelection.audioOutputDeviceId}
        isPrimaryTrainer={isPrimaryTrainer}
      >
        {children}
      </LiveKitBridge>
    </LiveKitRoom>
  );
}
